import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { InventoryAvailabilityDto} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { isAuthenticated, rememberMe, isGuest } = getSession(state);
    const userIsAuthenticated = (isAuthenticated || rememberMe) && !isGuest;
    return {
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        userIsAuthenticated,
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsWarehouseTableStyles {
    availability?: ProductAvailabilityStyles;
    actionsGridItemPriceSignIn?: GridItemProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableFirstHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    availabilityByWarehouseTableFirstCell?: DataTableCellBaseProps;
    container?: GridContainerProps;
    wrapper?: InjectableCss;
    inStockLabelText?: TypographyPresentationProps;
    totalStockLabelWrapper?: InjectableCss;
    totalStockValueText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const availabilityStyles: ProductDetailsWarehouseTableStyles = {
    availabilityByWarehouseTableFirstHeader: {
        css: css`
            width: 85%;
        `,
    },
    wrapper: {
        css: css`
            margin-top: 15px;
            text-align: center;
            max-height: 550px;
            overflow-y: auto;
        `,
    },
    availabilityByWarehouseTable: {
        css: css`
            table-layout: fixed;
        `,
    },
    availabilityByWarehouseTableHead: {
        css: css`
            position: sticky;
            top: 0;
            z-index: 1;
        `,
    },
    actionsGridItemPriceSignIn: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: center;
            display: inline-block;
            border: 1px solid;
            margin-bottom: auto !important;
            max-width: 100% !important;

            a {
                cursor: pointer;
                text-decoration: underline;
            }

            @media (max-width: 767px) {
                margin-bottom: 0 !important;
            }
        `,
    },
    availabilityByWarehouseTableFirstCell: {
        css: css`
            span {
                white-space: normal;
            }
        `,
    },
    totalStockLabelWrapper: {
        css: css`
            padding-top: 10px;

            @media (max-width: 768px) {
                display: flex;
                flex-direction: column;
            }
        `,
    },
    inStockLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    totalStockValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            padding-left: 5px;
            ${wrapInContainerStyles}

            @media (max-width: 768px) {
                padding-left: 0;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
};

const styles = availabilityStyles;

const ProductDetailsWarehouseTable = ({
    product,
    productInfo,
    userIsAuthenticated,
}: Props) => {
    const [warehousesAvailability, setWarehousesAvailability] = useState<InventoryAvailabilityDto[] | undefined>(
        undefined,
    );
    const [totalWarehouseStock, setTotalWarehouseStock] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    if (!product || !userIsAuthenticated) {
        return null;
    }

    useEffect(() => {
        if (product && productInfo.inventory && totalWarehouseStock === 0) {
            const warehouses = productInfo.inventory.inventoryAvailabilityDtos;
            const sortedWarehousesAvailability = warehouses
                ? [...warehouses].sort((a: InventoryAvailabilityDto, b: InventoryAvailabilityDto) => {
                      const messageA = a.availability?.message.toLowerCase() || "";
                      const messageB = b.availability?.message.toLowerCase() || "";
                      return messageA.localeCompare(messageB);
                  })
                : [];
            const totalWarehouse = (warehouses as unknown as InventoryAvailabilityDto[])?.find(warehouse =>
                warehouse.availability?.message.includes("Total"),
            );

            const totalMatch = totalWarehouse?.availability?.message.match(/Total:\s*(\d+)/);
            const totalValue = totalMatch ? parseInt(totalMatch[1], 10) : null;
            if (!Number.isNaN(totalValue) && totalValue !== null) {
                setTotalWarehouseStock(totalValue ?? 0);
            }
            setWarehousesAvailability(sortedWarehousesAvailability); // Type is InventoryAvailabilityDto[]
            setIsLoading(false);
        }
    }, [product, productInfo.inventory, totalWarehouseStock]);

    return (
        <>
            {isLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {!isLoading && (
                <>
                    <StyledWrapper {...styles.totalStockLabelWrapper}>
                        <Typography {...styles.inStockLabelText}>
                            {translate("Total In Stock in all warehouses")}:
                        </Typography>
                        <Typography {...styles.totalStockValueText} data-test-selector="inStockAllWarehouses">
                            {totalWarehouseStock}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.wrapper}>
                        <DataTable
                            {...styles.availabilityByWarehouseTable}
                            data-test-selector="availabilityByWarehouseTable"
                        >
                            <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                <DataTableHeader {...styles.availabilityByWarehouseTableFirstHeader}>
                                    {translate("Warehouse")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                    {translate("QTY")}
                                </DataTableHeader>
                            </DataTableHead>
                            {warehousesAvailability && warehousesAvailability.length > 0 && (
                                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                    {warehousesAvailability?.map((warehouse, index) => {
                                        const message = warehouse.availability?.message || "";
                                        const locationPart = message.includes("|")
                                            ? message.split("|")[1].trim()
                                            : message.trim();
                                        const location = locationPart.includes(":")
                                            ? locationPart.split(":")[0].trim()
                                            : locationPart;
                                        return (
                                            <DataTableRow {...styles.availabilityByWarehouseTableRow} key={index}>
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseTableFirstCell}
                                                    data-test-selector="warehouseAvailabilityTable_Description"
                                                >
                                                    {location}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseTableCell}
                                                    data-test-selector="warehouseAvailabilityTable_Quantity"
                                                >
                                                    {warehouse.availability?.message.split(":").pop()}
                                                </DataTableCell>
                                            </DataTableRow>
                                        );
                                    })}
                                </DataTableBody>
                            )}
                        </DataTable>
                    </StyledWrapper>
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsWarehouseTable)),
    definition: {
        displayName: "Warehouse Table",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;

// Verndale Custom Component : Ticket TGGMS-386
// Product Detail Page
// Show the Warehouse table on the PDP
